import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  TreeDeciduous,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  Home,
  Gift,
  Utensils,
  Frame,
  Hammer,
  Lamp,
  Box,
  Gem,
  Palette,
  Ruler,
  Armchair
} from 'lucide-react';
import { toast } from 'sonner';

// Product type options for woodworking
const PRODUCT_TYPES = [
  { value: 'tagliere', label: 'Tagliere / Vassoio', icon: Utensils },
  { value: 'cornice', label: 'Cornice / Specchiera', icon: Frame },
  { value: 'scultura', label: 'Scultura / Intaglio', icon: Hammer },
  { value: 'oggetto_decorativo', label: 'Oggetto Decorativo', icon: Lamp },
  { value: 'cofanetto', label: 'Cofanetto / Scatola', icon: Box },
  { value: 'portagioie', label: 'Portagioie / Portaorologi', icon: Gem },
  { value: 'mobile_piccolo', label: 'Piccolo Mobile / Mensola', icon: Home },
  { value: 'giocattolo', label: 'Giocattolo in Legno', icon: Heart },
  { value: 'utensile', label: 'Utensile da Cucina', icon: Utensils },
  { value: 'arredo', label: 'Elemento d\'Arredo', icon: Armchair },
  { value: 'altro', label: 'Altro', icon: TreeDeciduous },
];

// Wood type options
const WOOD_TYPES = [
  { value: 'ulivo', label: 'Legno di ulivo', description: 'Venature uniche' },
  { value: 'noce', label: 'Legno di noce', description: 'Elegante e scuro' },
  { value: 'ciliegio', label: 'Legno di ciliegio', description: 'Caldo e rossastro' },
  { value: 'rovere', label: 'Legno di rovere', description: 'Robusto e versatile' },
  { value: 'frassino', label: 'Legno di frassino', description: 'Chiaro e resistente' },
  { value: 'acero', label: 'Legno di acero', description: 'Chiaro e luminoso' },
  { value: 'castagno', label: 'Legno di castagno', description: 'Rustico e naturale' },
  { value: 'tiglio', label: 'Legno di tiglio', description: 'Ideale per intagli' },
  { value: 'mogano', label: 'Mogano', description: 'Pregiato e lussuoso' },
  { value: 'ebano', label: 'Ebano', description: 'Scuro e prezioso' },
  { value: 'abete_recupero', label: 'Abete antico / Recupero', description: 'Storia e carattere' },
  { value: 'non_so', label: 'Non so, vorrei consiglio', description: 'Ti guidiamo noi' },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'naturale_olio', label: 'Naturale con olio', icon: TreeDeciduous },
  { value: 'naturale_cera', label: 'Naturale con cera d\'api', icon: TreeDeciduous },
  { value: 'verniciato', label: 'Verniciato / Laccato', icon: Palette },
  { value: 'lucido', label: 'Lucidato a specchio', icon: Star },
  { value: 'satinato', label: 'Satinato / Semi-opaco', icon: Sparkles },
  { value: 'sbiancato', label: 'Sbiancato / Decapato', icon: Palette },
  { value: 'bruciato', label: 'Bruciato (Shou Sugi Ban)', icon: Zap },
  { value: 'anticato', label: 'Anticato / Patinato', icon: Clock },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: MessageSquare },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'mini', label: 'Miniatura (fino a 10 cm)', icon: Ruler },
  { value: 'piccolo', label: 'Piccolo (10-25 cm)', icon: Ruler },
  { value: 'medio', label: 'Medio (25-50 cm)', icon: Ruler },
  { value: 'grande', label: 'Grande (50-100 cm)', icon: Ruler },
  { value: 'xl', label: 'Extra Large (oltre 100 cm)', icon: Ruler },
  { value: 'personalizzato', label: 'Misure personalizzate', icon: Sparkles },
];

// Purpose options
const PURPOSE_OPTIONS = [
  { value: 'casa', label: 'Per arredare casa', icon: Home },
  { value: 'cucina', label: 'Per la cucina', icon: Utensils },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'matrimonio', label: 'Regalo matrimonio', icon: Heart },
  { value: 'commemorativo', label: 'Ricordo / Commemorativo', icon: Heart },
  { value: 'ristorante', label: 'Per ristorante / Attività', icon: Utensils },
  { value: 'ufficio', label: 'Per ufficio / Studio', icon: Home },
  { value: 'collezione', label: 'Per collezione', icon: Star },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '30-60', label: '€30 - €60', description: 'Oggetto semplice' },
  { value: '60-120', label: '€60 - €120', description: 'Legno pregiato' },
  { value: '120-250', label: '€120 - €250', description: 'Lavorazione dettagliata' },
  { value: '250-500', label: '€250 - €500', description: 'Pezzo importante' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Opera artigianale' },
  { value: '1000+', label: '€1.000+', description: 'Capolavoro / Grande formato' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: '2months', label: '2 Mesi', description: 'Opera complessa' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example woodworks for showcase
const WOODWORK_EXAMPLES = [
  {
    idea: "Tagliere grande per aperitivi con manico",
    result: "Tagliere in ulivo 60x30cm con manico integrato, canale raccogli-succo, finitura olio alimentare",
    image: "https://images.unsplash.com/photo-1624811533744-f85d5325d49c?w=400&h=300&fit=crop",
    category: "Tagliere"
  },
  {
    idea: "Cornice per specchio bagno stile rustico",
    result: "Specchiera 80x60cm in castagno di recupero, bordo live edge, trattamento anti-umidità",
    image: "https://images.unsplash.com/photo-1606627200742-460b55dd8351?w=400&h=300&fit=crop",
    category: "Cornice"
  },
  {
    idea: "Portagioie elegante per proposta matrimonio",
    result: "Cofanetto in ebano e acero 10x10cm con intarsio a cuore, interno in velluto bordeaux",
    image: "https://images.unsplash.com/photo-1749842839761-e5ae74f82a30?w=400&h=300&fit=crop",
    category: "Cofanetto"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato un tagliere in ulivo per il mio ristorante. Le venature sono incredibili e i clienti lo fotografano sempre! Qualità e bellezza uniche.",
    author: "Marco R.",
    location: "Bologna"
  },
  {
    text: "Cercavo una cornice per un quadro di famiglia molto speciale. Il falegname ha creato un pezzo che valorizza l'opera senza rubarle la scena. Perfetta!",
    author: "Giulia T.",
    location: "Firenze"
  },
  {
    text: "Il cofanetto per l'anello di fidanzamento era esattamente come lo immaginavo. L'artigiano ha capito subito cosa volevo. Lei ha pianto di gioia!",
    author: "Andrea B.",
    location: "Milano"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quale legno è migliore per il mio progetto?",
    answer: "Dipende dall'uso: l'ulivo è perfetto per taglieri (naturalmente antibatterico), il noce per mobili eleganti, il tiglio per sculture dettagliate, il rovere per pezzi robusti. L'artigiano ti consiglierà il legno ideale."
  },
  {
    question: "Posso richiedere incisioni o personalizzazioni?",
    answer: "Assolutamente sì! Incisioni laser, intagli a mano, intarsi con legni diversi, inserti in metallo o madreperla. Descrivi la tua idea e l'artigiano proporrà la tecnica migliore."
  },
  {
    question: "Il legno è trattato per uso alimentare?",
    answer: "Per taglieri e utensili da cucina si usano esclusivamente oli e finiture alimentari (olio di lino, cera d'api). L'artigiano ti fornirà anche le istruzioni per la manutenzione."
  },
  {
    question: "Quanto tempo ci vuole per un lavoro su misura?",
    answer: "Oggetti semplici: 1-2 settimane. Lavorazioni elaborate (intagli, intarsi, dorature): 3-6 settimane. Mobili o pezzi grandi: 1-2 mesi. L'artigiano ti darà tempistiche precise."
  },
  {
    question: "Posso vedere il legno prima della lavorazione?",
    answer: "Molti artigiani inviano foto del legno selezionato prima di iniziare, soprattutto per pezzi importanti. Potrai approvare venature e colorazione prima della lavorazione."
  }
];

export default function FalegnameriaRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_type: '',
    wood_type: '',
    finish: '',
    size: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Falegnameria Su Misura | Artigianato Italiano | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `wood-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi cosa vorresti realizzare');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_wood_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const woodLabel = WOOD_TYPES.find(t => t.value === formData.wood_type)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(t => t.value === formData.size)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (productLabel) details.push(`🪵  Tipo: ${productLabel}`);
      if (woodLabel) details.push(`🌳  Legno: ${woodLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel} in ${woodLabel}`.trim() || 'Falegnameria su misura',
        description: fullDescription,
        category: 'Falegnameria',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai falegnami.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('wood-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Falegnameria Su Misura | Artigianato Italiano in Legno | Rarifex</title>
        <meta name="description" content="Falegnameria artigianale italiana su misura: taglieri, cornici, sculture, cofanetti, oggetti decorativi in legno pregiato. Ulivo, noce, ciliegio. Gratuito e senza impegno." />
        <meta name="keywords" content="falegnameria su misura, artigianato legno, tagliere artigianale, cornice legno, scultura legno, cofanetto legno, oggetti legno, falegname italiano" />
        <link rel="canonical" href="https://www.rarifex.com/falegnameria-su-misura" />
        <meta property="og:title" content="Falegnameria Su Misura | Artigianato Italiano | Rarifex" />
        <meta property="og:description" content="Falegnameria artigianale italiana su misura. Taglieri, cornici, sculture, cofanetti in legno pregiato. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/falegnameria-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-stone-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <TreeDeciduous className="w-4 h-4" />
                    Falegnameria Artigianale
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il legno prende vita{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-stone-600">
                      nelle tue mani
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri falegnami italiani creano pezzi unici per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Taglieri, cornici, sculture, cofanetti, oggetti decorativi: qualsiasi cosa tu possa immaginare in legno.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Falegnami verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="wood-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Tagliere grande per aperitivi"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="wood-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi cosa vorresti realizzare *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Cosa vorresti? Che legno preferisci? Per cosa lo userai? Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="wood-description-textarea"
                        />
                      </div>
                      
                      {/* Product Type & Wood Type */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo</Label>
                          <select
                            value={formData.product_type}
                            onChange={(e) => handleInputChange('product_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="wood-type-select"
                          >
                            <option value="">Seleziona tipo...</option>
                            {PRODUCT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Legno</Label>
                          <select
                            value={formData.wood_type}
                            onChange={(e) => handleInputChange('wood_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="wood-wood-select"
                          >
                            <option value="">Seleziona...</option>
                            {WOOD_TYPES.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="wood-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="wood-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Budget */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="wood-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="wood-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          data-testid="wood-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-amber-600 to-stone-600 hover:from-amber-700 hover:to-stone-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="wood-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da falegnami verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai il tuo pezzo artigianale in legno
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci cosa vorresti creare' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da falegnami esperti' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il preventivo ideale' },
                { icon: TreeDeciduous, title: 'Ammira', desc: 'Il tuo pezzo in legno prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-100 to-stone-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT CAN BE MADE */}
        <section className="py-16 bg-gradient-to-b from-white to-amber-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Puoi Realizzare
              </h2>
              <p className="text-lg text-slate-600">
                I nostri falegnami creano qualsiasi oggetto in legno
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { icon: Utensils, label: 'Taglieri', color: 'bg-amber-100 text-amber-600' },
                { icon: Frame, label: 'Cornici', color: 'bg-stone-100 text-stone-600' },
                { icon: Hammer, label: 'Sculture', color: 'bg-orange-100 text-orange-600' },
                { icon: Lamp, label: 'Oggetti Decorativi', color: 'bg-emerald-100 text-emerald-600' },
                { icon: Box, label: 'Cofanetti', color: 'bg-violet-100 text-violet-600' },
                { icon: Gem, label: 'Portagioie', color: 'bg-pink-100 text-pink-600' },
                { icon: Armchair, label: 'Piccoli Mobili', color: 'bg-blue-100 text-blue-600' },
                { icon: Heart, label: 'Giocattoli', color: 'bg-red-100 text-red-600' },
              ].map((item, index) => (
                <div key={index} className={`${item.color} rounded-xl p-4 text-center`}>
                  <item.icon className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-medium text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Lavori Realizzati
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta all'oggetto finito
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {WOODWORK_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-gradient-to-b from-amber-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-amber-600 to-stone-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a creare qualcosa di unico in legno?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori falegnami italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="wood-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dai falegnami, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/login?redirect=/falegnameria-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
